import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Copy text in einer Weise, die auch in iframes mit gesperrter Clipboard-API
 * (z. B. Emergent-Preview) funktioniert. Reihenfolge:
 * 1. navigator.clipboard.writeText (modern, benötigt clipboard-write permission)
 * 2. document.execCommand("copy") über ein im DOM sichtbares Element
 *    (optional `sourceEl` ist robuster als eine temporäre versteckte Textarea)
 * 3. Letzter Fallback: temporäre Textarea
 * Wirft KEINE Exception nach außen — gibt `true` bei Erfolg, sonst `false` zurück.
 */
export async function copyText(text, sourceEl) {
  const value = String(text ?? "");
  // 1) Moderne API — in iframes mit Permissions-Policy oft blockiert
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (_) { /* fällt durch */ }

  // 2) Legacy via bereits sichtbares Input/Textarea — robuster als Hidden-Textarea,
  //    weil viele Browser execCommand("copy") nur in echten User-Gesten auf
  //    sichtbaren Elementen erlauben.
  try {
    if (
      sourceEl &&
      (sourceEl.tagName === "INPUT" || sourceEl.tagName === "TEXTAREA")
    ) {
      const prev = { start: sourceEl.selectionStart, end: sourceEl.selectionEnd };
      sourceEl.focus({ preventScroll: true });
      sourceEl.select();
      const ok = document.execCommand("copy");
      // Selection wieder herstellen, damit User nicht irritiert ist
      try { sourceEl.setSelectionRange(prev.start ?? 0, prev.end ?? 0); } catch (_) { /* noop */ }
      if (ok) return true;
    }
  } catch (_) { /* fällt durch */ }

  // 3) Letzter Fallback: temporäre Textarea (für Stellen ohne sichtbares Input)
  try {
    if (typeof document === "undefined") return false;
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    // sichtbar, aber außerhalb des Viewports — sonst lehnen Browser den Copy ab
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.width = "1em";
    ta.style.height = "1em";
    ta.style.padding = "0";
    ta.style.border = "0";
    ta.style.opacity = "0.01";
    document.body.appendChild(ta);
    ta.focus({ preventScroll: true });
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch (_) {
    return false;
  }
}
